import { ExternalLink, MessageCircle, Shield, ShieldOff } from "lucide-solid";
import { Show } from "solid-js";
import {
  DUEL_STATUS_VARIANT,
  REPORT_STATUS_VARIANT,
  SEAL_LABELS,
  reportPermissions,
  sealType,
  type Duel,
  type DuelResultSubmission,
  type Report,
} from "system";
import { MatchTimeline } from "~/components/match-timeline";
import { PageContainer, PageHeader } from "~/components/layout/page";
import {
  DismissDialog,
  EscalateButton,
  ForceDialog,
  OpenDisputeDialog,
  ReleaseButton,
  TakeReportButton,
  hostOf,
} from "~/components/report-actions";
import { TierBadge } from "~/components/tier-badge";
import { Avatar } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { duelsApi, reportsApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { requireFeature } from "~/lib/features";
import { QueryKey } from "~/lib/query-keys";
import { CAR_CLASS_LABELS, RACE_TYPE_LABELS, cn, formatDateTime } from "~/lib/utils";

import { createQuery } from "@tanstack/solid-query";
import { Link, createFileRoute } from "@tanstack/solid-router";

export const Route = createFileRoute("/_authed/reports/$reportId")({
  // requireFeature only gates the feature flag; the judge/admin role check lives in
  // the component (mirrors the admin panel).
  beforeLoad: () => requireFeature("admin"),
  component: ReportDetailPage,
});

function ReportDetailPage() {
  const params = Route.useParams();
  const { state } = useAuth();
  const isJudge = () =>
    state.user?.role === "judge" || state.user?.role === "admin";

  // Keyed under the [Reports] prefix so the action mutations (which invalidate
  // [Reports]) refresh this detail without a bespoke key.
  const report = createQuery(() => ({
    queryKey: [QueryKey.Reports, params().reportId],
    queryFn: () => reportsApi.get(params().reportId),
    enabled: !!params().reportId,
  }));

  return (
    <Show when={isJudge()} fallback={<Restricted />}>
      <PageContainer>
        <PageHeader title="Report" description="Investigate and resolve a driver report">
          <Link
            to="/admin"
            class="text-sm text-brand hover:underline shrink-0"
          >
            ← Back to panel
          </Link>
        </PageHeader>

        <Show
          when={report.data}
          keyed
          fallback={
            <Show
              when={report.isError}
              fallback={<div class="h-64 bg-surface-2 rounded-xl animate-pulse" />}
            >
              <p class="text-center text-text-faint py-20">
                This report could not be found.
              </p>
            </Show>
          }
        >
          {(r) => <ReportDetail report={r} />}
        </Show>
      </PageContainer>
    </Show>
  );
}

function ReportDetail(props: { report: Report }) {
  const { state } = useAuth();
  const r = () => props.report;
  const perms = () => reportPermissions(props.report, state.user);

  // Two columns on wide screens: the report on the left, the duel and its match
  // history on the right (so the judge rarely needs to open the duel page). Folds to
  // a single stacked column on mobile, and on a duel-less report stays single too.
  return (
    <div class={cn("grid items-start gap-6", r().duelId && "lg:grid-cols-2")}>
      {/* Left: the report itself. */}
      <div class="space-y-5">
        {/* Status, who filed it, and when. */}
        <div class="flex items-center gap-3 flex-wrap">
          <Badge variant={REPORT_STATUS_VARIANT[r().status]}>
            {r().status.replace("_", " ")}
          </Badge>
          <Show when={r().reporter}>
            <Link
              to="/profile/$username"
              params={{ username: r().reporter!.username }}
              search={{ tab: "duels" }}
              class="inline-flex items-center gap-2 hover:text-brand"
            >
              <Avatar
                src={r().reporter?.avatarUrl ?? undefined}
                fallback={r().reporter?.displayName}
                size="sm"
              />
              <span class="text-sm font-semibold text-text-base">
                {r().reporter?.displayName}
              </span>
            </Link>
          </Show>
          <span class="text-xs text-text-faint">
            filed {formatDateTime(r().createdAt)}
          </span>
        </div>

        {/* The complaint, verbatim — newlines preserved for score-conflict bullets. */}
        <div>
          <p class="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1.5">
            Issue
          </p>
          <p class="text-sm text-text-base whitespace-pre-line">{r().issue}</p>
        </div>

        <Show when={r().evidenceLink}>
          <div>
            <p class="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1.5">
              Evidence
            </p>
            <a
              href={r().evidenceLink!}
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 text-sm text-brand hover:underline"
            >
              <ExternalLink size={13} /> {hostOf(r().evidenceLink!)}
            </a>
          </div>
        </Show>

        <Show when={r().assignedJudge}>
          <p class="text-xs text-text-faint">
            Taken by{" "}
            <span class="text-text-muted font-medium">
              {r().assignedJudge?.displayName}
            </span>
          </p>
        </Show>

        <Show when={r().resolution}>
          <div class="rounded-lg border border-border bg-surface-2 px-3 py-2.5">
            <p class="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">
              Resolution
            </p>
            <p class="text-sm text-text-muted">{r().resolution}</p>
            <Show when={r().resolvedAt}>
              <p class="text-xs text-text-faint mt-1">
                {formatDateTime(r().resolvedAt!)}
              </p>
            </Show>
          </div>
        </Show>

        {/* Actions, grouped by consequence and gated to who may act. */}
        <Show when={perms().isParticipant && perms().isActionable}>
          <p class="inline-flex items-center gap-1.5 text-xs text-text-faint italic">
            <ShieldOff size={12} />
            You're in this duel, so another judge will take it.
          </p>
        </Show>

        <Show when={r().status === "open" && !perms().isParticipant}>
          <div class="flex items-center gap-2 pt-3 border-t border-border-subtle">
            <TakeReportButton reportId={r().id} />
          </div>
        </Show>

        <Show when={perms().canConclude}>
          <div class="flex flex-col gap-3 pt-3 border-t border-border-subtle">
            {/* A finalized duel is immutable: it can't be reopened into a dispute, so
                a violation is penalized with the ranking tools instead. */}
            <Show when={r().duel?.status === "completed"}>
              <p class="text-xs text-text-faint">
                This duel is finalized, so it can't be reopened or forced. Dismiss if
                unfounded; a real violation is penalized with the ranking tools
                (disqualify or tier correction).
              </p>
            </Show>
            <div class="flex items-center gap-2 flex-wrap">
              <Show when={r().duel?.status !== "completed"}>
                <OpenDisputeDialog reportId={r().id} duelId={r().duelId} />
              </Show>
              <Show
                when={
                  r().duel?.status === "expired" ||
                  r().duel?.status === "declined"
                }
              >
                <ForceDialog reportId={r().id} duelId={r().duelId} />
              </Show>
              <DismissDialog reportId={r().id} />
              <Show when={r().status === "under_review" && perms().isMine}>
                <span class="ml-auto inline-flex items-center gap-2">
                  <Show when={!perms().isAdmin}>
                    <EscalateButton reportId={r().id} />
                  </Show>
                  <ReleaseButton reportId={r().id} />
                </span>
              </Show>
            </div>

            {/* Investigation is off-platform: DM both drivers directly in Discord. */}
            <Show when={r().duel?.attacker && r().duel?.defender}>
              <div class="flex items-center gap-3 text-xs text-text-faint">
                Contact:
                <a
                  href={`https://discord.com/users/${r().duel!.attacker!.discordId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-brand hover:underline"
                >
                  <MessageCircle size={11} /> {r().duel!.attacker!.displayName}
                </a>
                <a
                  href={`https://discord.com/users/${r().duel!.defender!.discordId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-brand hover:underline"
                >
                  <MessageCircle size={11} /> {r().duel!.defender!.displayName}
                </a>
              </div>
            </Show>
          </div>
        </Show>
      </div>

      {/* Right (folds under on mobile): the duel and its match history, so the
          judge can rule without leaving the report. */}
      <Show when={r().duelId}>
        <DuelSummary duelId={r().duelId!} />
      </Show>
    </div>
  );
}

// The full duel inline: status, matchup with tiers, the per-match timeline (or the
// conflicting score claims for a score-only duel), and timing. Fetched separately
// because the report payload only carries the duel's basic matchup.
function DuelSummary(props: { duelId: string }) {
  const duel = createQuery(() => ({
    queryKey: [QueryKey.Duel, props.duelId],
    queryFn: () => duelsApi.get(props.duelId),
    enabled: !!props.duelId,
  }));

  return (
    <div class="rounded-xl border border-border bg-surface-2 p-4">
      <Show
        when={duel.data}
        keyed
        fallback={<div class="h-40 animate-pulse rounded-lg bg-surface-3" />}
      >
        {(d) => (
          <div class="space-y-3">
            {/* Header: status, category, matchup band, recording mode, escape hatch. */}
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
              <Badge variant={DUEL_STATUS_VARIANT[d.status]}>
                {d.status.replace("_", " ")}
              </Badge>
              <span class="font-semibold uppercase tracking-wide text-text-faint">
                {RACE_TYPE_LABELS[d.raceType]} · {CAR_CLASS_LABELS[d.carClass]}
              </span>
              <Show when={sealType(d.attackerTier, d.defenderTier)}>
                {(s) => (
                  <span class="text-text-muted">
                    {SEAL_LABELS[s()].kanji} {SEAL_LABELS[s()].label}
                  </span>
                )}
              </Show>
              <Badge variant="default">
                {d.recordingMode === "full" ? "Per-match" : "Score only"}
              </Badge>
              <Link
                to="/duels/$duelId"
                params={{ duelId: d.id }}
                class="ml-auto inline-flex items-center gap-1 text-brand hover:underline"
              >
                Full duel page →
              </Link>
            </div>

            {/* Matchup with tiers. */}
            <div class="flex items-center gap-2 text-sm">
              <div class="flex flex-1 min-w-0 items-center justify-end gap-2">
                <span class="truncate font-semibold text-text-base">
                  {d.attacker?.displayName ?? "?"}
                </span>
                <Show when={d.attackerTier}>
                  {(t) => <TierBadge tier={t()} size="sm" />}
                </Show>
                <Avatar
                  src={d.attacker?.avatarUrl ?? undefined}
                  fallback={d.attacker?.displayName}
                  size="sm"
                  class="shrink-0"
                />
              </div>
              <span class="shrink-0 text-xs font-semibold text-text-faint">VS</span>
              <div class="flex flex-1 min-w-0 items-center gap-2">
                <Avatar
                  src={d.defender?.avatarUrl ?? undefined}
                  fallback={d.defender?.displayName}
                  size="sm"
                  class="shrink-0"
                />
                <Show when={d.defenderTier}>
                  {(t) => <TierBadge tier={t()} size="sm" />}
                </Show>
                <span class="truncate font-semibold text-text-base">
                  {d.defender?.displayName ?? "?"}
                </span>
              </div>
            </div>

            <Show when={d.forced}>
              <p class="text-center text-xs text-text-faint">
                A judge forced this challenge to be raced.
              </p>
            </Show>

            {/* Per-match duels carry the round timeline (which includes the score
                bar); score-only duels show the agreed score and each side's claim. */}
            <Show
              when={d.recordingMode === "full"}
              fallback={
                <div class="space-y-3 border-t border-border-subtle pt-3">
                  <div class="text-center">
                    <p class="font-display text-2xl font-bold text-text-base whitespace-nowrap">
                      {d.attackerScore}{" "}
                      <span class="text-sm text-text-faint">–</span>{" "}
                      {d.defenderScore}
                    </p>
                    <Show when={outcomeLine(d)}>
                      {(o) => <p class="text-xs text-text-faint">{o()}</p>}
                    </Show>
                  </div>
                  <Show when={(d.resultSubmissions?.length ?? 0) > 0}>
                    <div class="grid grid-cols-2 gap-3">
                      <ClaimCard
                        name={d.attacker?.displayName}
                        sub={d.resultSubmissions?.find(
                          (s) => s.submitterId === d.attackerId,
                        )}
                      />
                      <ClaimCard
                        name={d.defender?.displayName}
                        sub={d.resultSubmissions?.find(
                          (s) => s.submitterId === d.defenderId,
                        )}
                      />
                    </div>
                  </Show>
                </div>
              }
            >
              <div class="border-t border-border-subtle pt-3">
                <MatchTimeline
                  matches={d.matches ?? []}
                  status={d.status}
                  attacker={d.attacker!}
                  defender={d.defender!}
                  attackerScore={d.attackerScore}
                  defenderScore={d.defenderScore}
                />
              </div>
            </Show>

            {/* Timing. */}
            <div class="flex flex-wrap gap-x-4 gap-y-1 border-t border-border-subtle pt-3 text-xs text-text-faint">
              <span>Challenged {formatDateTime(d.createdAt)}</span>
              <Show when={d.status === "pending"}>
                <span>Respond by {formatDateTime(d.respondDeadline)}</span>
              </Show>
              <Show when={d.status === "active"}>
                <span>Due {formatDateTime(d.duelDeadline)}</span>
              </Show>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}

// One driver's submitted score in a score-only duel; the two side by side surface a
// conflict at a glance. Shows a dash when that driver hasn't submitted yet.
function ClaimCard(props: { name?: string; sub?: DuelResultSubmission }) {
  return (
    <div class="rounded-lg bg-surface-3 p-3 text-center">
      <p class="mb-1 text-xs text-text-faint">{props.name} submitted</p>
      <Show
        when={props.sub}
        keyed
        fallback={<p class="text-sm text-text-faint">—</p>}
      >
        {(sub) => (
          <p class="font-display text-xl font-bold text-text-base whitespace-nowrap">
            {sub.attackerScore} - {sub.defenderScore}
          </p>
        )}
      </Show>
    </div>
  );
}

// One-line outcome for a settled duel: who won (and whether by forfeit), or a draw.
function outcomeLine(d: Duel): string | undefined {
  if (d.status !== "completed") return undefined;
  if (!d.winnerId) return "Draw, defender keeps their tier";
  const winner = d.winnerId === d.attackerId ? d.attacker : d.defender;
  const byForfeit = d.attackerForfeited || d.defenderForfeited;
  return `${winner?.displayName ?? "?"} won${byForfeit ? " by forfeit" : ""}`;
}

function Restricted() {
  return (
    <PageContainer>
      <div class="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <Shield size={40} class="text-text-faint" />
        <h2 class="font-display text-xl font-semibold text-text-base">
          Restricted
        </h2>
        <p class="text-sm text-text-muted max-w-xs">
          Only judges and admins can access this panel.
        </p>
      </div>
    </PageContainer>
  );
}
